import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "../lib/supabaseClient";
import "./CollaboratorsManager.css";

const CollaboratorsManager = ({ userId, userPlan, collectionId }) => {
  const { t } = useTranslation();
  const [collaborators, setCollaborators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [toast, setToast] = useState(null);
  const [linkModal, setLinkModal] = useState({ open: false, url: "", id: null });
  const [activatingId, setActivatingId] = useState(null);
  const [collections, setCollections] = useState([]);
  const [selectedCollectionIds, setSelectedCollectionIds] = useState([]);
  // Plafond de clients du plan Art Advisor : lu en base (v_advisor_entitlements.max_clients), jamais en dur.
  const [advisorMaxClients, setAdvisorMaxClients] = useState(5);

  useEffect(() => {
    if (userPlan !== "art_advisor" || !userId) return undefined;
    let cancelled = false;
    supabase.from("v_advisor_entitlements").select("max_clients").eq("advisor_id", userId).maybeSingle()
      .then(({ data }) => { if (!cancelled && Number.isFinite(data?.max_clients)) setAdvisorMaxClients(data.max_clients); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [userPlan, userId]);

  // ✅ compact: toggle détails par ligne
  const [expandedId, setExpandedId] = useState(null);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2200);
  };

  const [editingCollaborator, setEditingCollaborator] = useState(null);
  const [formData, setFormData] = useState({
    role: "team_member",
    email: "",
    first_name: "",
    last_name: "",
    phone: "",
    can_view: true,
    can_edit: false,
    can_add_artworks: false,
    can_delete_artworks: false,
    can_export: false,
    can_generate_reports: false,
    can_access_ai: false,
    notes: "",
  });

  // Limites selon le plan
  const PLAN_LIMITS = {
    standard: { team_readonly: 1, total: 1 },
    premium: { team_member: 5, expert: 999 },
    art_advisor: { client: advisorMaxClients, assistant: 1 },
    art_advisor_premium: { client: 999, assistant: 3 },
    family_office: { family_office_user: 999 },
    fondation: { art_advisor: 999, family_office_user: 999 },
  };

  // Rôles disponibles selon le plan
  const AVAILABLE_ROLES = {
    standard: [
      {
        value: "team_readonly",
        labelKey: "teamReadonly",
        descriptionKey: "teamReadonly",
      },
    ],
    premium: [
      {
        value: "team_member",
        labelKey: "teamMember",
        descriptionKey: "teamMember",
      },
      {
        value: "expert",
        labelKey: "expert",
        descriptionKey: "expert",
      },
    ],
    art_advisor: [
      {
        value: "client",
        labelKey: "collectorClient",
        descriptionKey: "collectorClientLimited",
      },
      {
        value: "assistant",
        labelKey: "assistant",
        descriptionKey: "assistantOne",
      },
    ],
    art_advisor_premium: [
      {
        value: "client",
        labelKey: "collectorClient",
        descriptionKey: "collectorClientUnlimited",
      },
      {
        value: "assistant",
        labelKey: "assistant",
        descriptionKey: "assistant",
      },
    ],
    family_office: [
      {
        value: "family_office_user",
        labelKey: "familyOfficeUser",
        descriptionKey: "familyOfficeUser",
      },
    ],
    fondation: [
      {
        value: "art_advisor",
        labelKey: "artAdvisor",
        descriptionKey: "artAdvisor",
      },
      {
        value: "family_office_user",
        labelKey: "familyOffice",
        descriptionKey: "familyOffice",
      },
    ],
  };

const pickDefaultRoleForPlan = (plan) => {
  // si le plan autorise "expert", on le choisit
  const roles = AVAILABLE_ROLES?.[plan] || [];
  if (roles.some((r) => r.value === "expert")) return "expert";
  if (roles.some((r) => r.value === "team_member")) return "team_member";
  if (roles.some((r) => r.value === "team_readonly")) return "team_readonly";
  // fallback
  return roles?.[0]?.value || "team_member";
};

  useEffect(() => {
    fetchCollections();
    fetchCollaborators();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, collectionId]);
useEffect(() => {
  const applyInvite = (payload) => {
    const email = payload?.email?.trim();
    if (!email) return;

    setFormData((prev) => ({
      ...prev,
      email,
      role: pickDefaultRoleForPlan(userPlan),
      can_view: true,
      can_edit: false,
      can_add_artworks: false,
      can_delete_artworks: false,
      can_export: false,
      can_generate_reports: false,
      can_access_ai: false,
      notes: prev.notes || "",
    }));

    setSelectedCollectionIds(collectionId ? [collectionId] : []);
    setEditingCollaborator(null);
    setShowModal(true);
    showToast(t("collaborators.toasts.inviteReady"));
  };

  // 1) écoute event live
  const onEvt = (evt) => applyInvite(evt.detail);
  window.addEventListener("artkiv:invite-collaborator", onEvt);

  // 2) fallback localStorage (si l’utilisateur arrive ensuite sur Collaborateurs)
  try {
    const raw = localStorage.getItem("artkiv_pending_collaborator_invite");
    if (raw) {
      const payload = JSON.parse(raw);
      localStorage.removeItem("artkiv_pending_collaborator_invite");
      applyInvite(payload);
    }
  } catch (e) {}

  return () => window.removeEventListener("artkiv:invite-collaborator", onEvt);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [userPlan, collectionId]);

  const getCollectionOwnerId = (collection) => {
    if (!collection) return null;

    return (
      collection.client_id ||
      collection.user_id ||
      collection.owner_id ||
      collection.created_by ||
      collection.profile_id ||
      null
    );
  };

  const getClientDisplayName = (profile, fallbackId = null) => {
    if (!profile) {
      return t("collaborators.fallbacks.unknownClient");
    }

    const company =
      profile.company_name ||
      profile.company ||
      profile.organisation ||
      profile.organization ||
      "";

    if (company?.trim()) return company.trim();

    const first = profile.first_name || profile.firstname || "";
    const last = profile.last_name || profile.lastname || "";
    const fullName = `${first} ${last}`.trim();

    if (fullName) return fullName;

    // Fallback sur l'email (sans le domaine pour rester lisible)
    if (profile._email) {
      const emailPart = profile._email.split("@")[0];
      return emailPart || profile._email;
    }

    return t("collaborators.fallbacks.unknownClient");
  };

  const getCollectionLabel = (collection) => {
    if (!collection) return t("collaborators.fallbacks.untitledCollection");
    return (
      collection.name ||
      collection.title ||
      collection.label ||
      collection.collection_name ||
      collection.description ||
      t("collaborators.fallbacks.collectionWithId", { id: String(collection.id || "").slice(0, 8) })
    );
  };

  const getCollectionDisplayLabel = (collection) => {
    if (!collection) return `${t("collaborators.fallbacks.untitledCollection")} — ${t("collaborators.fallbacks.unknownClient")}`;

    const label = getCollectionLabel(collection);
    const ownerId = getCollectionOwnerId(collection);
    const clientName = getClientDisplayName(collection.client_profile, ownerId);

    return `${label} — ${clientName}`;
  };

  const fetchCollections = async () => {
    try {
      const { data, error } = await supabase
        .from("collections")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;

      const rawCollections = data || [];
      const ownerIds = [
        ...new Set(
          rawCollections
            .map((collection) => getCollectionOwnerId(collection))
            .filter(Boolean)
        ),
      ];

      let profilesById = {};

      if (ownerIds.length) {
        // 1. Chercher dans profiles
        const { data: profilesData } = await supabase
          .from("profiles")
          .select("id, first_name, last_name, company, company_name")
          .in("id", ownerIds);

        profilesById = (profilesData || []).reduce((acc, p) => {
          acc[p.id] = p;
          return acc;
        }, {});

        // 2. Fallback : compléter avec v_expert_my_clients (email + nom) pour les IDs non trouvés
        const missingIds = ownerIds.filter((id) => !profilesById[id]);
        if (missingIds.length) {
          const { data: clientsData } = await supabase
            .rpc("get_expert_my_clients")
            .select("client_id, first_name, last_name, email, company")
            .in("client_id", missingIds);

          (clientsData || []).forEach((c) => {
            if (c.client_id && !profilesById[c.client_id]) {
              profilesById[c.client_id] = {
                id: c.client_id,
                first_name: c.first_name,
                last_name: c.last_name,
                company: c.company,
                // utiliser l'email comme fallback si pas de nom
                _email: c.email,
              };
            }
          });
        }
      }

      const hydratedCollections = rawCollections.map((collection) => {
        const ownerId = getCollectionOwnerId(collection);
        return {
          ...collection,
          client_profile: ownerId ? profilesById[ownerId] || null : null,
        };
      });

      setCollections(hydratedCollections);

      if (collectionId) {
        setSelectedCollectionIds((prev) =>
          prev.length ? prev : [collectionId]
        );
      }
    } catch (error) {
      console.error("Error fetching collections:", error);
      setCollections([]);
    }
  };

  const toggleSelectedCollection = (id) => {
    setSelectedCollectionIds((prev) =>
      prev.includes(id) ? prev.filter((cid) => cid !== id) : [...prev, id]
    );
  };

  const buildCollaboratorPayload = () => ({
    role: formData.role,
    email: formData.email?.trim() || "",
    first_name: formData.first_name || "",
    last_name: formData.last_name || "",
    phone: formData.phone || "",
    can_view: !!formData.can_view,
    can_edit: !!formData.can_edit,
    can_add_artworks: !!formData.can_add_artworks,
    can_delete_artworks: !!formData.can_delete_artworks,
    can_export: !!formData.can_export,
    can_generate_reports: !!formData.can_generate_reports,
    can_access_ai: !!formData.can_access_ai,
    notes: formData.notes || "",
    collection_id: selectedCollectionIds[0] || collectionId || null,
    created_by: userId,
  });

  const syncCollectionAccess = async (collaboratorId) => {
    if (!collaboratorId) return;

    const { error: deleteError } = await supabase
      .from("collaborator_collection_access")
      .delete()
      .eq("collaborator_id", collaboratorId);

    if (deleteError) throw deleteError;

    if (!selectedCollectionIds.length) return;

    const rows = selectedCollectionIds.map((cid) => ({
      collaborator_id: collaboratorId,
      collection_id: cid,
    }));

    const { error: insertError } = await supabase
      .from("collaborator_collection_access")
      .insert(rows);

    if (insertError) throw insertError;
  };

  const fetchCollaborators = async () => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from("collaborators")
        .select("*")
        .eq("created_by", userId)
        .order("created_at", { ascending: false });

      if (error) throw error;

      const baseCollaborators = data || [];
      const ids = baseCollaborators.map((c) => c.id).filter(Boolean);
      let accessRows = [];

      if (ids.length) {
        const { data: accessData, error: accessError } = await supabase
          .from("collaborator_collection_access")
          .select("collaborator_id, collection_id")
          .in("collaborator_id", ids);

        if (accessError) throw accessError;
        accessRows = accessData || [];
      }

      const accessByCollaborator = accessRows.reduce((acc, row) => {
        if (!acc[row.collaborator_id]) acc[row.collaborator_id] = [];
        acc[row.collaborator_id].push(row.collection_id);
        return acc;
      }, {});

      const hydrated = baseCollaborators.map((collab) => ({
        ...collab,
        accessible_collection_ids:
          accessByCollaborator[collab.id] ||
          (collab.collection_id ? [collab.collection_id] : []),
      }));

      const filtered = collectionId
        ? hydrated.filter(
            (collab) =>
              collab.collection_id === collectionId ||
              collab.accessible_collection_ids.includes(collectionId)
          )
        : hydrated;

      setCollaborators(filtered);
    } catch (error) {
      console.error("Error fetching collaborators:", error);
      showToast(t("collaborators.errors.load", { message: error.message }));
    } finally {
      setLoading(false);
    }
  };

  const checkLimits = (role) => {
    const limits = PLAN_LIMITS[userPlan];
    if (!limits) return { allowed: false, message: t("collaborators.errors.unknownPlan") };

    const currentCount = collaborators.filter(
      (c) => c.role === role && c.status !== "revoked"
    ).length;

    const limit = Number.isFinite(limits[role])
      ? limits[role]
      : Number.isFinite(limits.total)
        ? limits.total
        : 999;

    if (currentCount >= limit) {
      return {
        allowed: false,
        message: t("collaborators.errors.roleLimit", { current: currentCount, limit }),
      };
    }

    return { allowed: true };
  };

  const applyPreset = (preset) => {
    if (preset === "readonly") {
      setFormData((p) => ({
        ...p,
        can_view: true,
        can_edit: false,
        can_add_artworks: false,
        can_delete_artworks: false,
        can_export: false,
        can_generate_reports: false,
        can_access_ai: false,
      }));
    }
    if (preset === "assistant") {
      setFormData((p) => ({
        ...p,
        can_view: true,
        can_edit: true,
        can_add_artworks: true,
        can_delete_artworks: false,
        can_export: true,
        can_generate_reports: true,
        can_access_ai: false,
      }));
    }
    if (preset === "expert") {
      setFormData((p) => ({
        ...p,
        can_view: true,
        can_edit: false,
        can_add_artworks: false,
        can_delete_artworks: false,
        can_export: false,
        can_generate_reports: true,
        can_access_ai: true,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!editingCollaborator) {
      const limitCheck = checkLimits(formData.role);
      if (!limitCheck.allowed) {
        showToast(limitCheck.message);
        return;
      }
    }

    try {
      if (!selectedCollectionIds.length && collections.length > 0) {
        showToast(t("collaborators.errors.selectCollection"));
        return;
      }

      if (editingCollaborator) {
        const collaboratorData = {
          ...buildCollaboratorPayload(),
          status: editingCollaborator.status,
          user_id: editingCollaborator.user_id,
          invitation_token: editingCollaborator.invitation_token,
          invitation_sent_at: editingCollaborator.invitation_sent_at,
          invitation_accepted_at: editingCollaborator.invitation_accepted_at,
        };

        const { error } = await supabase
          .from("collaborators")
          .update(collaboratorData)
          .eq("id", editingCollaborator.id);

        if (error) throw error;
        await syncCollectionAccess(editingCollaborator.id);
      } else {
        const collaboratorData = {
          ...buildCollaboratorPayload(),
          status: "pending",
          invitation_sent_at: new Date().toISOString(),
        };

        const { data: inserted, error } = await supabase
          .from("collaborators")
          .insert([collaboratorData])
          .select("id")
          .single();

        if (error) throw error;

        if (inserted?.id) {
          await syncCollectionAccess(inserted.id);

          const { error: resendErr } = await supabase.rpc(
            "resend_collaborator_invitation",
            { p_collaborator_id: inserted.id }
          );
          if (resendErr) throw resendErr;
        }
      }

      fetchCollaborators();
      resetForm();
      setShowModal(false);
    } catch (error) {
      console.error("Error saving collaborator:", error);
      showToast(t("collaborators.errors.generic", { message: error.message }));
    }
  };

  const handleRevoke = async (id) => {
    if (!window.confirm(t("collaborators.confirm.revoke")))
      return;

    try {
      const { error } = await supabase.rpc("revoke_collaborator", {
        p_collaborator_id: id,
        p_reason: t("collaborators.audit.revokeReason"),
      });
      if (error) throw error;
      fetchCollaborators();
      if (expandedId === id) setExpandedId(null);
    } catch (error) {
      console.error("Error revoking collaborator:", error);
      showToast(t("collaborators.errors.generic", { message: error.message }));
    }
  };

  const handleResend = async (id) => {
    try {
      const { error } = await supabase.rpc("resend_collaborator_invitation", {
        p_collaborator_id: id,
      });
      if (error) throw error;
      fetchCollaborators();
      showToast(t("collaborators.toasts.invitationResent"));
      const { data: tokenData } = await supabase
        .from("collaborators").select("invitation_token").eq("id", id).single();
      if (tokenData?.invitation_token) {
        const url = `${window.location.origin}/collaborators/accept?token=${encodeURIComponent(tokenData.invitation_token)}`;
        setLinkModal({ open: true, url, id });
      }
    } catch (error) {
      console.error("Error resending invitation:", error);
      showToast(t("collaborators.errors.resend", { message: error.message || t("collaborators.errors.unknown") }));
    }
  };

  const handleCopyInviteLink = async (collaborator) => {
    try {
      const { data, error } = await supabase
        .from("collaborators")
        .select("invitation_token")
        .eq("id", collaborator.id)
        .single();

      if (error) throw error;

      const token = data?.invitation_token;
      if (!token) {
        showToast(
          t("collaborators.errors.noToken")
        );
        return;
      }

      const url = `${window.location.origin}/collaborators/accept?token=${encodeURIComponent(token)}`;
      try {
        await navigator.clipboard.writeText(url);
        showToast(t("collaborators.toasts.linkCopied"));
      } catch {
        setLinkModal({ open: true, url, id: collaborator.id });
      }
    } catch (e) {
      console.error("Error copying invite link:", e);
      showToast(t("collaborators.errors.copyLink"));
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      const { error } = await supabase
        .from("collaborators")
        .update({ status: newStatus })
        .eq("id", id);

      if (error) throw error;
      fetchCollaborators();
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };

  const normalizeRpcResult = (result) => {
    if (Array.isArray(result)) return result[0] || null;
    return result || null;
  };

  // Activation manuelle d'un collaborateur "pending" dont l'utilisateur s'est enregistré
  const handleActivate = async (id) => {
    if (activatingId) return;
    if (!window.confirm(t("collaborators.confirm.activate"))) return;

    setActivatingId(id);
    try {
      showToast(t("collaborators.toasts.activating"));

      // RPC SECURITY DEFINER : résout user_id depuis auth.users + email, puis active l'accès.
      const { data, error } = await supabase.rpc("activate_collaborator", {
        p_collaborator_id: id,
      });

      if (error) throw error;

      const result = normalizeRpcResult(data);
      if (result && result.success === false) {
        showToast(result.error || t("collaborators.errors.activateImpossible"));
        return;
      }

      await fetchCollaborators();
      showToast(t("collaborators.toasts.activated"));
    } catch (error) {
      console.error("Error activating collaborator:", error);
      showToast(t("collaborators.errors.activate", { message: error.message || t("collaborators.errors.unknown") }));
    } finally {
      setActivatingId(null);
    }
  };

  const resetForm = () => {
    setFormData({
      role: "team_member",
      email: "",
      first_name: "",
      last_name: "",
      phone: "",
      can_view: true,
      can_edit: false,
      can_add_artworks: false,
      can_delete_artworks: false,
      can_export: false,
      can_generate_reports: false,
      can_access_ai: false,
      notes: "",
    });
    setSelectedCollectionIds(collectionId ? [collectionId] : []);
    setEditingCollaborator(null);
  };

  const handleEdit = (collaborator) => {
    setFormData({
      role: collaborator.role || "team_member",
      email: collaborator.email || "",
      first_name: collaborator.first_name || "",
      last_name: collaborator.last_name || "",
      phone: collaborator.phone || "",
      can_view: !!collaborator.can_view,
      can_edit: !!collaborator.can_edit,
      can_add_artworks: !!collaborator.can_add_artworks,
      can_delete_artworks: !!collaborator.can_delete_artworks,
      can_export: !!collaborator.can_export,
      can_generate_reports: !!collaborator.can_generate_reports,
      can_access_ai: !!collaborator.can_access_ai,
      notes: collaborator.notes || "",
    });
    setSelectedCollectionIds(
      collaborator.accessible_collection_ids?.length
        ? collaborator.accessible_collection_ids
        : collaborator.collection_id
          ? [collaborator.collection_id]
          : []
    );
    setEditingCollaborator(collaborator);
    setShowModal(true);
  };

  const getRoleLabel = (role) => {
    const roles = AVAILABLE_ROLES[userPlan] || [];
    const found = roles.find((r) => r.value === role);
    return found ? t(`collaborators.roles.labels.${found.labelKey}`) : role;
  };

  const getRoleDescription = (role) =>
    t(`collaborators.roles.descriptions.${role.descriptionKey}`);

  const getStatusLabel = (status) => {
    const key = { pending: "pending", active: "active", suspended: "suspended", revoked: "revoked" }[status];
    return key ? t(`collaborators.statuses.${key}`) : status;
  };

  const stats = {
    total: collaborators.length,
    active: collaborators.filter((c) => c.status === "active").length,
    pending: collaborators.filter((c) => c.status === "pending").length,
  };

  return (
    <div className="collaborators-manager compact">

      {/* Header compact */}
      <div className="collaborators-header compact">
        <div className="header-left">
          <div className="title-row">
            <h2>{t("collaborators.title")}</h2>
            <div className="chips">
              <span className="chip">{t("collaborators.stats.total", { count: stats.total })}</span>
              <span className="chip chip-ok">{t("collaborators.stats.active", { count: stats.active })}</span>
              <span className="chip chip-warn">{t("collaborators.stats.pending", { count: stats.pending })}</span>
            </div>
          </div>
          <div className="subtitle compact">
            {t("collaborators.subtitle")}
          </div>
        </div>

        <button
          className="btn-add-collaborator compact"
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          type="button"
        >
          + {t("collaborators.actions.invite")}
        </button>
      </div>

      {/* Liste des collaborateurs */}
      {loading ? (
        <div className="loading compact">{t("collaborators.common.loading")}</div>
      ) : collaborators.length === 0 ? (
        <div className="empty-state compact">
          <div className="empty-icon">👥</div>
          <div className="empty-copy">
            <div className="empty-title">{t("collaborators.empty.title")}</div>
            <div className="empty-text">
              {t("collaborators.empty.text")}
            </div>
            <div className="empty-actions">
              <button
                className="btn-add-collaborator compact"
                onClick={() => {
                  resetForm();
                  setShowModal(true);
                }}
                type="button"
              >
                + {t("collaborators.actions.invite")}
              </button>

              <button
                className="btn-secondary compact"
                onClick={() => {
                  resetForm();
                  setFormData((p) => ({
                    ...p,
                    role: AVAILABLE_ROLES[userPlan]?.[0]?.value || "team_member",
                  }));
                  setShowModal(true);
                }}
                type="button"
              >
                {t("collaborators.actions.assistantAccess")}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="collaborators-list compact">
          {/* ✅ FIX: restore map so "collaborator" exists */}
          {collaborators.map((collaborator) => (
            <div key={collaborator.id} className="collaborator-row">
              <div className="row-left">
                <div className="collaborator-avatar small">
                  {(
                    collaborator.first_name?.[0] ||
                    collaborator.email?.[0] ||
                    "?"
                  ).toUpperCase()}
                </div>

                <div className="row-main">
                  <div className="row-line1">
                    <span className="name">
                      {collaborator.first_name || collaborator.last_name
                        ? `${collaborator.first_name || ""} ${
                            collaborator.last_name || ""
                          }`.trim()
                        : collaborator.email}
                    </span>

                    <span className="badges">
                      <span className="role-badge small">
                        {getRoleLabel(collaborator.role)}
                      </span>
                      <span
                        className={`status-badge small status-${collaborator.status}`}
                      >
                        {getStatusLabel(collaborator.status)}
                      </span>
                    </span>
                  </div>

                  <div className="row-line2">
                    <span className="email small">{collaborator.email}</span>

                    <button
                      className="btn-link small"
                      type="button"
                      onClick={() =>
                        setExpandedId(
                          expandedId === collaborator.id
                            ? null
                            : collaborator.id
                        )
                      }
                    >
                      {expandedId === collaborator.id ? t("collaborators.actions.hide") : t("collaborators.actions.details")}
                    </button>
                  </div>
                </div>
              </div>

              <div className="row-actions">
                {collaborator.status === "pending" && (
                  <>
                    <button
                      className="btn-icon btn-activate"
                      onClick={() => handleActivate(collaborator.id)}
                      disabled={activatingId === collaborator.id}
                      title={t("collaborators.tooltips.activate")}
                      type="button"
                      style={{ color: "#065f46", fontWeight: 700, opacity: activatingId === collaborator.id ? 0.55 : 1 }}
                    >
                      {activatingId === collaborator.id ? "…" : "✓"}
                    </button>
                    <button
                      className="btn-icon"
                      onClick={() => handleResend(collaborator.id)}
                      title={t("collaborators.tooltips.resend")}
                      type="button"
                    >
                      🔁
                    </button>
                    <button
                      className="btn-icon"
                      onClick={() => handleCopyInviteLink(collaborator)}
                      title={t("collaborators.tooltips.copyLink")}
                      type="button"
                    >
                      🔗
                    </button>
                  </>
                )}

                <button
                  className="btn-icon"
                  onClick={() => handleEdit(collaborator)}
                  title={t("collaborators.tooltips.edit")}
                  type="button"
                >
                  ✏️
                </button>

                {collaborator.status === "active" && (
                  <button
                    className="btn-icon"
                    onClick={() =>
                      handleStatusChange(collaborator.id, "suspended")
                    }
                    title={t("collaborators.tooltips.suspend")}
                    type="button"
                  >
                    ⏸️
                  </button>
                )}

                {collaborator.status === "suspended" && (
                  <button
                    className="btn-icon"
                    onClick={() =>
                      handleStatusChange(collaborator.id, "active")
                    }
                    title={t("collaborators.tooltips.reactivate")}
                    type="button"
                  >
                    ▶️
                  </button>
                )}

                <button
                  className="btn-icon btn-delete"
                  onClick={() => handleRevoke(collaborator.id)}
                  title={t("collaborators.tooltips.revoke")}
                  type="button"
                >
                  🗑️
                </button>
              </div>

              {expandedId === collaborator.id && (
                <div className="row-details">
                  <div className="permissions-inline">
                    {collaborator.can_view && (
                      <span className="permission-dot">👁️</span>
                    )}
                    {collaborator.can_edit && (
                      <span className="permission-dot">✏️</span>
                    )}
                    {collaborator.can_add_artworks && (
                      <span className="permission-dot">➕</span>
                    )}
                    {collaborator.can_delete_artworks && (
                      <span className="permission-dot">🗑️</span>
                    )}
                    {collaborator.can_export && (
                      <span className="permission-dot">📥</span>
                    )}
                    {collaborator.can_generate_reports && (
                      <span className="permission-dot">📊</span>
                    )}
                    {collaborator.can_access_ai && (
                      <span className="permission-dot">🤖</span>
                    )}

                    <span className="permissions-text">
                      {t("collaborators.permissions.label")}: {[
                        collaborator.can_view && t("collaborators.permissions.short.view"),
                        collaborator.can_edit && t("collaborators.permissions.short.edit"),
                        collaborator.can_add_artworks && t("collaborators.permissions.short.add"),
                        collaborator.can_delete_artworks && t("collaborators.permissions.short.delete"),
                        collaborator.can_export && t("collaborators.permissions.short.export"),
                        collaborator.can_generate_reports && t("collaborators.permissions.short.reports"),
                        collaborator.can_access_ai && t("collaborators.permissions.short.ai"),
                      ]
                        .filter(Boolean)
                        .join(", ") || t("collaborators.permissions.none")}
                    </span>
                  </div>

                  <div className="notes-mini">
                    {t("collaborators.collections.label")}: {collaborator.accessible_collection_ids?.length
                      ? collaborator.accessible_collection_ids
                          .map((cid) =>
                            getCollectionDisplayLabel(collections.find((c) => c.id === cid))
                          )
                          .join(", ")
                      : t("collaborators.collections.noneDefined")}
                  </div>

                  {collaborator.notes ? (
                    <div className="notes-mini">{collaborator.notes}</div>
                  ) : null}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal Ajout/Édition */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {editingCollaborator ? t("collaborators.modal.editTitle") : t("collaborators.modal.inviteTitle")}
              </h2>
              <button className="btn-close" onClick={() => setShowModal(false)}>
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="collaborator-form">
              {/* Rôle */}
              <div className="form-group">
                <label>{t("collaborators.form.role")} *</label>
                <select
                  value={formData.role}
                  onChange={(e) =>
                    setFormData({ ...formData, role: e.target.value })
                  }
                  required
                  disabled={editingCollaborator}
                >
                  {(AVAILABLE_ROLES[userPlan] || []).map((role) => (
                    <option key={role.value} value={role.value}>
                      {t(`collaborators.roles.labels.${role.labelKey}`)} - {getRoleDescription(role)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Email */}
              <div className="form-group">
                <label>{t("collaborators.form.email")} *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  required
                  placeholder="email@example.com"
                />
              </div>

              {/* Nom/Prénom */}
              <div className="form-row">
                <div className="form-group">
                  <label>{t("collaborators.form.firstName")}</label>
                  <input
                    type="text"
                    value={formData.first_name}
                    onChange={(e) =>
                      setFormData({ ...formData, first_name: e.target.value })
                    }
                    placeholder={t("collaborators.form.firstName")}
                  />
                </div>
                <div className="form-group">
                  <label>{t("collaborators.form.lastName")}</label>
                  <input
                    type="text"
                    value={formData.last_name}
                    onChange={(e) =>
                      setFormData({ ...formData, last_name: e.target.value })
                    }
                    placeholder={t("collaborators.form.lastName")}
                  />
                </div>
              </div>

              {/* Téléphone */}
              <div className="form-group">
                <label>{t("collaborators.form.phone")}</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                  placeholder="+33 6 12 34 56 78"
                />
              </div>

              {/* Collections accessibles */}
              <div className="form-group">
                <label>{t("collaborators.form.accessibleCollections")} *</label>
                <div className="subtitle compact" style={{ marginBottom: 10 }}>
                  {t("collaborators.form.collectionsHint")}
                </div>

                {collections.length === 0 ? (
                  <div className="empty-text">
                    {t("collaborators.form.noCollectionsLoaded")}
                  </div>
                ) : (
                  <div className="permissions-grid">
                    {collections.map((collection) => (
                      <label key={collection.id} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={selectedCollectionIds.includes(collection.id)}
                          onChange={() => toggleSelectedCollection(collection.id)}
                        />
                        <span>
                          <span>{getCollectionLabel(collection)}</span>
                          <span
                            style={{
                              marginLeft: 6,
                              fontSize: 12,
                              color: "#6b7280",
                            }}
                          >
                            — {getClientDisplayName(
                              collection.client_profile,
                              getCollectionOwnerId(collection)
                            )}
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Permissions */}
              {formData.role !== "team_readonly" && (
                <div className="form-group">
                  <label>{t("collaborators.permissions.label")}</label>

                  <div className="preset-row">
                    <button
                      type="button"
                      className="btn-preset"
                      onClick={() => applyPreset("readonly")}
                    >
                      {t("collaborators.presets.readonly")}
                    </button>
                    <button
                      type="button"
                      className="btn-preset"
                      onClick={() => applyPreset("assistant")}
                    >
                      {t("collaborators.presets.assistant")}
                    </button>
                    <button
                      type="button"
                      className="btn-preset"
                      onClick={() => applyPreset("expert")}
                    >
                      {t("collaborators.presets.expert")}
                    </button>
                  </div>

                  <div className="permissions-grid">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={!!formData.can_view}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            can_view: e.target.checked,
                          })
                        }
                      />
                      <span>👁️ {t("collaborators.permissions.view")}</span>
                    </label>

                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={!!formData.can_edit}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            can_edit: e.target.checked,
                          })
                        }
                      />
                      <span>✏️ {t("collaborators.permissions.edit")}</span>
                    </label>

                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={!!formData.can_add_artworks}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            can_add_artworks: e.target.checked,
                          })
                        }
                      />
                      <span>➕ {t("collaborators.permissions.add")}</span>
                    </label>

                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={!!formData.can_delete_artworks}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            can_delete_artworks: e.target.checked,
                          })
                        }
                      />
                      <span>🗑️ {t("collaborators.permissions.delete")}</span>
                    </label>

                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={!!formData.can_export}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            can_export: e.target.checked,
                          })
                        }
                      />
                      <span>📥 {t("collaborators.permissions.export")}</span>
                    </label>

                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={!!formData.can_generate_reports}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            can_generate_reports: e.target.checked,
                          })
                        }
                      />
                      <span>📊 {t("collaborators.permissions.reports")}</span>
                    </label>

                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={!!formData.can_access_ai}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            can_access_ai: e.target.checked,
                          })
                        }
                      />
                      <span>🤖 {t("collaborators.permissions.ai")}</span>
                    </label>
                  </div>
                </div>
              )}

              {/* Notes */}
              <div className="form-group">
                <label>{t("collaborators.form.notes")}</label>
                <textarea
                  value={formData.notes || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, notes: e.target.value })
                  }
                  placeholder={t("collaborators.form.notesPlaceholder")}
                  rows="3"
                />
              </div>

              {/* Actions */}
              <div className="form-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setShowModal(false)}
                >
                  {t("collaborators.actions.cancel")}
                </button>
                <button type="submit" className="btn-submit">
                  {editingCollaborator ? t("collaborators.actions.update") : t("collaborators.actions.sendInvitation")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {linkModal.open && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.45)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}
          onClick={() => setLinkModal({ open:false, url:"", id:null })}>
          <div style={{ background:"#fff", padding:32, maxWidth:560, width:"100%", borderRadius:2, boxShadow:"0 20px 40px rgba(0,0,0,0.15)", fontFamily:"system-ui,sans-serif" }}
            onClick={e => e.stopPropagation()}>
            <h3 style={{ fontFamily:"Georgia,serif", fontWeight:400, fontSize:20, color:"#1a1a1a", marginBottom:8 }}>{t("collaborators.linkModal.title")}</h3>
            <p style={{ fontSize:13, color:"#6b6b6b", marginBottom:16, lineHeight:1.6 }}>
              {t("collaborators.linkModal.description")}
            </p>
            <div style={{ display:"flex", gap:8, alignItems:"stretch" }}>
              <input readOnly value={linkModal.url}
                style={{ flex:1, padding:"10px 12px", border:"1px solid #e5e3df", borderRadius:2, fontSize:12, fontFamily:"monospace", color:"#1a1a1a", background:"#fafaf8" }}
                onFocus={e => e.target.select()}/>
              <button type="button"
                style={{ padding:"10px 16px", background:"#1a1a1a", color:"#fff", border:"none", borderRadius:2, fontSize:13, cursor:"pointer", whiteSpace:"nowrap" }}
                onClick={async () => {
                  try { await navigator.clipboard.writeText(linkModal.url); showToast(t("collaborators.toasts.linkCopied")); }
                  catch { showToast(t("collaborators.errors.copyManually")); }
                }}>{t("collaborators.actions.copy")}</button>
            </div>
            <button type="button"
              style={{ marginTop:20, padding:"8px 16px", background:"transparent", border:"1px solid #e5e3df", borderRadius:2, cursor:"pointer", fontSize:13, color:"#4a4a4a" }}
              onClick={() => setLinkModal({ open:false, url:"", id:null })}>{t("collaborators.actions.close")}</button>
          </div>
        </div>
      )}

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
};

export default CollaboratorsManager;